import React, { useEffect, useState } from 'react';
import { Task } from '../../../../types/Task';
import { taskService } from '../../../../services/quantifiedTaskService';
import { useAuth } from '../../../../contexts/AuthContext';
import { Button, Alert, Form } from '@/components/ui';
import ResponsiveModal from '../ResponsiveModal';
import { getEffortCount } from '../../utils';
import { describeActionError } from '../../../../utils/loadError';

const COMMENTS_FIELD_ID = 'internal-review-comments';

interface SetInternalReviewModalProps {
  visible: boolean;
  onClose: () => void;
  task: Task;
  onSubmit: (comments: string) => Promise<void>;
}

const SetInternalReviewModal: React.FC<SetInternalReviewModalProps> = ({
  visible,
  onClose,
  task,
  onSubmit,
}) => {
  const [comments, setComments] = useState('');
  const [loading, setLoading] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (visible) {
      setCommentsError(null);
      setSubmitError(null);
    }
  }, [visible]);

  // Check if task has any efforts. getEffortCount, not `!task.efforts` - this modal receives a row
  // from the task list, and list rows carry only the server's effortSummary. See the equivalent
  // note in CloseTaskModal.
  const hasNoEfforts = () => getEffortCount(task) === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if task has no efforts and no comments - require comments
    if (hasNoEfforts() && !comments.trim()) {
      setCommentsError('Comments required when no efforts are recorded');
      setTimeout(() => document.getElementById(COMMENTS_FIELD_ID)?.focus(), 0);
      return;
    }
    
    try {
      setLoading(true);
      setSubmitError(null);
      await taskService.setToInternalReview(task.id, {
        comments,
        userId: currentUser?.id
      });
      onSubmit(comments);
      onClose();
    } catch (error) {
      console.error('Error setting task to internal review:', error);
      setSubmitError(describeActionError(error, "Couldn't set the task to internal review. Try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title="Set Task to Internal Review"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            onClick={handleSubmit}
            disabled={loading || !comments.trim()}
          >
            {loading ? 'Submitting...' : 'Submit'}
          </Button>
        </>
      }
    >
      {submitError && (
        <Alert variant="danger" role="alert">
          {submitError}
        </Alert>
      )}
      <Alert variant="info">
        <i className="bi bi-info-circle tw-mr-2"></i>
        Please provide a reason for setting this task back to internal review.
      </Alert>

      {hasNoEfforts() && (
        <Alert variant="warning">
          <i className="bi bi-exclamation-triangle tw-mr-2"></i>
          <strong>Warning:</strong> This task has no efforts recorded. Comments are required to explain why no efforts were recorded before moving to internal review.
        </Alert>
      )}

      <Form.Group className="tw-mb-3">
        <Form.Label>
          Comments
          {hasNoEfforts() && <span className="tw-text-danger tw-ml-1">*</span>}
        </Form.Label>
        <Form.Control
          id={COMMENTS_FIELD_ID}
          as="textarea"
          rows={4}
          value={comments}
          onChange={(e) => {
            setComments(e.target.value);
            if (commentsError) setCommentsError(null);
          }}
          isInvalid={!!commentsError}
          aria-invalid={!!commentsError}
          aria-describedby={commentsError ? `${COMMENTS_FIELD_ID}-error` : undefined}
          required
          placeholder={hasNoEfforts() ? "Comments are required when no efforts are recorded. Please explain why..." : "Please provide your comments..."}
        />
        {commentsError && (
          <Form.Control.Feedback type="invalid" id={`${COMMENTS_FIELD_ID}-error`}>
            {commentsError}
          </Form.Control.Feedback>
        )}
        {hasNoEfforts() && (
          <div className="tw-mt-1 tw-text-sm tw-text-content-muted">
            <small>Comments are mandatory when moving a task to internal review without any efforts.</small>
          </div>
        )}
      </Form.Group>
    </ResponsiveModal>
  );
};

export default SetInternalReviewModal; 